import { useMemo, useState } from "react";

import {
  ChipList,
  StatusBadge,
  TypeBadge,
} from "@/app/library/components/molecules";
import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import type { APILibraryTemplatePackResponseType } from "@/core/data_sources/models/APILibraryTemplatePack";
import type { APILibraryVerticalResponseType } from "@/core/data_sources/models/APILibraryVertical";
import { Badge, Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  AdminPanel,
  Banner,
} from "@/core/ui/molecules";

type CreateTemplatePackDialogProps = {
  readonly onClose: () => void;
  readonly packs: readonly APILibraryTemplatePackResponseType[];
  readonly templates: readonly APILibraryTemplateResponseType[];
  readonly verticals: readonly APILibraryVerticalResponseType[];
};

export function CreateTemplatePackDialog({
  onClose,
  packs,
  templates,
  verticals,
}: CreateTemplatePackDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("published");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<number>>(
    () => new Set(),
  );
  const visibleTemplates = useMemo(
    () =>
      templates.filter(
        (template) =>
          (typeFilter === "all" || template.type === typeFilter) &&
          (statusFilter === "all" || template.status === statusFilter),
      ),
    [statusFilter, templates, typeFilter],
  );
  const isValid = name.trim().length > 0 && selectedTemplateIds.size > 0;

  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] w-[1180px] max-w-[96vw] flex-col overflow-hidden rounded-dialog bg-surface shadow-dialog">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-5">
        <div>
          <h2 className="text-lg font-extrabold text-ink">
            Create Template Pack
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">
            Build a simple onboarding pack: metadata + selected templates. Packs
            group published templates for tenant installation.
          </p>
        </div>
        <button
          className="text-2xl text-ink-subtle"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label>
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Pack name
            </span>
            <input
              className={adminInputClasses}
              onChange={(event) => setName(event.target.value)}
              placeholder="HVAC Starter Pack"
              value={name}
            />
          </label>
          <label>
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Core for vertical
            </span>
            <select className={adminSelectClasses}>
              <option>None</option>
              {verticals.map((vertical) => (
                <option key={vertical.code}>{vertical.name}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
            Description
          </span>
          <textarea
            className={`${adminInputClasses} min-h-24 w-full py-2.5`}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What this pack installs and when it should be used."
            value={description}
          />
        </label>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <AdminPanel className="p-3.5">
            <div className="mb-2 text-xs font-extrabold text-ink-muted">
              Allowed Verticals
            </div>
            <ChipList values={verticals.map((vertical) => vertical.name)} />
          </AdminPanel>
          <AdminPanel className="p-3.5">
            <div className="mb-2 text-xs font-extrabold text-ink-muted">
              Allowed Plans
            </div>
            <ChipList values={["Growth"]} />
          </AdminPanel>
        </div>

        <div className="mb-2 mt-5 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-[15px] font-extrabold text-ink">
            Templates included
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={adminSelectClasses}
              onChange={(event) => setTypeFilter(event.target.value)}
              value={typeFilter}
            >
              <option value="all">All types</option>
              <option value="form">Form</option>
              <option value="media">Media</option>
              <option value="model">Model</option>
            </select>
            <select
              className={adminSelectClasses}
              onChange={(event) => setStatusFilter(event.target.value)}
              value={statusFilter}
            >
              <option value="all">All status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
            <Badge tone="info">{selectedTemplateIds.size} selected</Badge>
          </div>
        </div>
        <Banner>
          <strong>V1 rule:</strong> normally select only Published templates.
          Draft templates are shown only so the demo can explain why they should
          not be published inside a pack.
        </Banner>
        <div className="overflow-hidden rounded-control border border-border bg-surface">
          {visibleTemplates.map((template) => (
            <label
              className="grid grid-cols-[28px_minmax(0,1fr)_auto_auto] items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0 hover:bg-panel-muted"
              key={String(template.id ?? template.name)}
            >
              <input
                checked={
                  typeof template.id === "number" &&
                  selectedTemplateIds.has(template.id)
                }
                onChange={(event) => {
                  if (typeof template.id !== "number") {
                    return;
                  }
                  setSelectedTemplateIds((prev) => {
                    const next = new Set(prev);
                    if (event.target.checked) {
                      next.add(template.id!);
                    } else {
                      next.delete(template.id!);
                    }
                    return next;
                  });
                }}
                type="checkbox"
              />
              <span>
                <strong>{template.name}</strong>
                <span className="block text-xs text-ink-muted">
                  {template.type} · v{template.version ?? "-"} ·{" "}
                  {template.categories?.[0]?.name ?? "-"} ·{" "}
                  {template.verticals
                    ?.map((vertical) => vertical.name)
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </span>
              <TypeBadge type={template.type} />
              <StatusBadge status={template.status} />
            </label>
          ))}
        </div>
        <details className="mt-4 rounded-panel border border-border bg-panel-muted p-3.5">
          <summary className="cursor-pointer font-extrabold text-ink">
            Existing active packs
          </summary>
          <div className="mt-3 space-y-2">
            {packs
              .filter((pack) => pack.status === "active")
              .map((pack) => (
                <label
                  className="flex items-center justify-between gap-3 rounded-control border border-border bg-surface px-3 py-2.5"
                  key={String(pack.id ?? pack.name)}
                >
                  <span>
                    <strong>{pack.name}</strong>
                    <span className="block text-xs text-ink-muted">
                      {(pack.templates ?? []).length} templates
                    </span>
                  </span>
                  <input type="checkbox" />
                </label>
              ))}
          </div>
        </details>
        <Banner tone={isValid ? "ok" : "warning"}>
          {isValid ? (
            <>
              <strong>Ready.</strong> Pack can be saved as disabled or active.
            </>
          ) : (
            <>
              <strong>Required:</strong> name, at least one vertical and at
              least one template.
            </>
          )}
        </Banner>
      </div>
      <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-5 py-3.5">
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={!isValid}>Save Disabled</Button>
        <Button disabled={!isValid} variant="primary">
          Save Active
        </Button>
      </footer>
    </div>
  );
}
